import * as React from 'react';
import { NormalPeoplePicker as UIFabricNormalPeoplePicker, ValidationState } from '@fluentui/react/lib/Pickers';
import { IPersonaProps, Persona, PersonaSize } from '@fluentui/react';

// Custom Fabric UI
import { ISuggestionItemProps } from '@fluentui/react/src/components/pickers/Suggestions/SuggestionsItem.types';
import { Icon } from '@fluentui/react/lib/Icon';

import { TooltipLabel } from '../TooltipLabel';

interface Props {
    brandName: string;
    tooltipAriaLabel?: string;
    tooltipLabel?: string;
    suggestionsHeaderText?: string;
    noResultsFoundText?: string;
    suggestionsLimit?: number;
    disabled?: boolean;
    onInputChange?(input: string): string;
    onResolveSuggestions(
        filterText: string,
        currentPersonas: IPersonaProps[],
        limitResults?: number
    ): IPersonaProps[] | Promise<IPersonaProps[]>;
    onItemSelected(selected: IPersonaProps): IPersonaProps | Promise<IPersonaProps> | null;
    onChange?(items: IPersonaProps[]): void;
    onRenderSuggestionsItem?(item: IPersonaProps, suggestionItemProps: ISuggestionItemProps<IPersonaProps>): React.ReactElement;
}

const PeoplePickerComponent = (props: Props): React.ReactElement => {
    const {
        brandName,
        tooltipAriaLabel,
        tooltipLabel,
        suggestionsHeaderText,
        noResultsFoundText,
        suggestionsLimit,
        disabled,
        onInputChange,
        onResolveSuggestions,
        onItemSelected,
        onChange,
        onRenderSuggestionsItem,
    } = props;

    const _validateIsEmail = (suggestion: string) : ValidationState => {
        if (suggestion.indexOf('>') >= 0 || suggestion.indexOf('<') >= 0) {
            return ValidationState.invalid;
        }

        const emailRegEx = /([a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g;
        const emailAddress = suggestion.match(emailRegEx);
        if (emailAddress && emailAddress[0]) {
            return ValidationState.valid;
        }
        return ValidationState.warning;
    }

    const _onInputChange = (input: string): string => {
        const outlookRegEx = /<([a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)>/g;
        const emailAddress = input.match(outlookRegEx);
        if (!emailAddress || !emailAddress[0]) {
            return input;
        }
        const emails = emailAddress.map(x => x.substring(1, x.length - 1)).join(';');

        if ( onInputChange && typeof onInputChange === 'function' )
            onInputChange(input);
        return emails;
    };

    const _onRenderInitials = (item: IPersonaProps) => {
        if (!item.id) {
            return {
                onRenderInitials: () => (
                    <Icon iconName='AddFriend' styles={{ root: { color: 'root' } }} />
                )
            };
        }
        return {};
    }

    const _onRenderSuggestionsItem = (item: IPersonaProps, suggestionItemProps: ISuggestionItemProps<IPersonaProps>): React.ReactElement => {
        const onRenderInitials = _onRenderInitials(item);
        return (
            <div style={{ height: 72, display: 'flex', alignItems: 'center' }}>
                <React.Fragment>
                    {(item.id || _validateIsEmail(item.primaryText) === ValidationState.valid) ?
                        <Persona
                            coinSize={32}
                            {...onRenderInitials}
                            size={PersonaSize.size72}
                            primaryText={item.primaryText}
                            onRenderPrimaryText={(personaProps: IPersonaProps) => {
                                return (
                                    <React.Fragment>
                                        {item.id ? (
                                            personaProps.primaryText
                                        ) : (
                                            <React.Fragment>
                                                Invite <b>{personaProps.primaryText && personaProps.primaryText.length > 40 ? personaProps.primaryText.substring(0,40) + '...' : personaProps.primaryText}</b> to join {brandName}
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                );
                            }}
                            secondaryText={item.id ? item.secondaryText : ''}
                            tertiaryText={item.tertiaryText}
                            styles={{
                                root: {
                                    paddingLeft: 12
                                },
                                primaryText: {
                                    fontSize: 14
                                },
                                secondaryText: {
                                    fontSize: 12
                                },
                                tertiaryText: {
                                    fontSize: 12
                                }
                            }}
                        />
                        :
                        <div style={{paddingLeft: 12}}>No email detected</div>
                    }
                </React.Fragment>
            </div>

        );
    };

    // Either use inbuilt function or prop function
    const _onRenderSuggestionsItemFunc = onRenderSuggestionsItem && typeof onRenderSuggestionsItem === 'function' ? onRenderSuggestionsItem:_onRenderSuggestionsItem;

    return (
        <div>
            {tooltipAriaLabel && tooltipLabel && (
                <TooltipLabel ariaLabel={tooltipAriaLabel} required={false} label={tooltipLabel} />
            )}
            <UIFabricNormalPeoplePicker
                pickerSuggestionsProps={{
                    loadingText: 'Loading...',
                    noResultsFoundText: noResultsFoundText || 'we couldn\'t find any members',
                    suggestionsHeaderText: suggestionsHeaderText || 'Suggested people',
                }}
                inputProps={{
                    placeholder: 'Start typing a member name or email address',
                    autoComplete: 'off',
                }}
                resolveDelay={700}
                onValidateInput={_validateIsEmail}
                //createGenericItem={this.createGenericItem}
                onInputChange={_onInputChange}
                itemLimit={suggestionsLimit || 10}
                disabled={disabled || false}
                onResolveSuggestions={onResolveSuggestions}
                onItemSelected={onItemSelected}
                onChange={onChange}
                onRenderSuggestionsItem={_onRenderSuggestionsItemFunc}
            />
        </div>
    );
};

export default PeoplePickerComponent;